// Admin console: the last alerts one account was sent, and on which channels.
import React, { useState } from 'react';

// Hawaii time, same as the alerts themselves say. e.g. "Oct 28, 9:43 PM".
function fmtHst(ms) {
  if (!ms) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'Pacific/Honolulu', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(new Date(ms));
  } catch { return '—'; }
}

const SEND_MARK = {
  sent:   { mark: '✓', cls: 'admin-sends__mark--sent', title: 'Sent' },
  failed: { mark: '✕', cls: 'admin-sends__mark--failed', title: 'Tried, did not go out' },
};

function SendMark({ outcome }) {
  const m = SEND_MARK[outcome];
  if (!m) return <span className="admin-sends__mark" title="Not sent (off)" aria-label="Not sent">—</span>;
  return <span className={`admin-sends__mark ${m.cls}`} title={m.title} aria-label={m.title}>{m.mark}</span>;
}

/**
 * The table itself. `showWho` adds Device and User columns for the
 * fleet-wide list; the per-account list already knows both.
 */
export function SendsTable({ rows, showWho, multiTank }) {
  return (
    <div className="admin-sends__wrap">
      <table className="admin-sends__table">
        <thead>
          <tr>
            <th>Alert</th>
            {showWho && <th>Device</th>}
            {showWho && <th>User</th>}
            <th>Threshold</th><th className="admin-sends__ch">Email</th><th className="admin-sends__ch">Push</th><th className="admin-sends__ch">Text</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => {
            const ch = e.channels || {};
            const tank = (showWho ? e.multiTank : multiTank) && e.tankName ? ` · ${e.tankName}` : '';
            return (
              <tr key={`${e.deviceId || ''}-${e.id || `${e.ms}-${e.label}`}`}>
                <td>
                  <div className="admin-sends__name">{e.label}{tank}</div>
                  <div className="admin-sends__time">{fmtHst(e.ms)}</div>
                </td>
                {showWho && (
                  <td>
                    <div className="admin-sends__name">{e.deviceName || e.deviceId}</div>
                    {e.deviceName && <div className="admin-sends__time">{e.deviceId}</div>}
                  </td>
                )}
                {showWho && <td className="admin-sends__user">{e.userName || '—'}</td>}
                <td className="admin-sends__thr">{e.threshold || '—'}</td>
                <td className="admin-sends__ch"><SendMark outcome={ch.email} /></td>
                <td className="admin-sends__ch"><SendMark outcome={ch.push} /></td>
                <td className="admin-sends__ch"><SendMark outcome={ch.sms} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Every account's recent sends across the fleet, newest first. Each account's
 * own last 5 per device is enough: the fleet's newest N are always among them.
 */
export function latestAcrossFleet(devices, n = 5) {
  const all = [];
  (devices || []).forEach((d) => {
    const multiTank = Array.isArray(d.tanks) && d.tanks.filter(Boolean).length > 1;
    (d.claims || []).forEach((c) => {
      (c.recentSends || []).forEach((e) => all.push({
        ...e,
        deviceId: d.deviceId,
        deviceName: d.name || null,
        userName: c.displayName || c.email || null,
        multiTank,
      }));
    });
  });
  return all.sort((a, b) => (b.ms || 0) - (a.ms || 0)).slice(0, n);
}

/**
 * The last 5 alerts this person was actually sent, and on which channels.
 * Answers "I never got a text" without digging through logs: ✕ means we
 * tried and it didn't go out (bad email, no push device left, opted out).
 * Starts empty — only alerts sent after this log went in are recorded.
 */
export default function RecentAlerts({ sends, multiTank }) {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(sends) ? sends : [];
  return (
    <div className="admin-sends">
      <button type="button" className="admin-sends__toggle" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}>
        <span className={`admin-sends__chev ${open ? 'admin-sends__chev--open' : ''}`} aria-hidden="true">▸</span>
        Recent alerts sent
        <span className="admin-sends__count">{list.length ? `last ${list.length}` : 'none yet'}</span>
      </button>
      {open && (list.length === 0 ? (
        <div className="admin-sends__empty">No alerts sent to this account since the send log started.</div>
      ) : (
        <SendsTable rows={list} multiTank={multiTank} />
      ))}
    </div>
  );
}
